import { gatewayWebSocketUrl } from "lib/consts/arcGateway";
import { isHttpSecure } from "lib/utils/utils";

/** Port exposed by both the ARC and arc_proxy for live_audio_sink WebSockets. */
const DEFAULT_LIVE_AUDIO_PORT = "8765";
/**
 * WebSocket URL for the live_audio_sink service on the robot.
 *
 * Two routing shapes, matching how live_audio_sink is exposed:
 *  - **Fleet mode** (`arcId` set): go through arc_proxy at
 *    `wss://<proxy-host>/arc/<serial>/api/audio`.
 *  - **ARC mode** (`arcId` null): same-origin `ws://<gateway-origin>/api/audio`
 *    through arc_gateway, with the ARC JWT as `?token=` (a browser cannot set
 *    an Authorization header on a WebSocket handshake).
 *
 * `REACT_APP_LIVE_AUDIO_WS_URL` overrides everything. Host resolution otherwise mirrors
 * getStcmMapListBaseUrl. Fleet mode always uses wss://; direct mode follows
 * the supplied security flag.
 *
 * NOTE: direct ARC mode is plain HTTP, which is not a secure context, so
 * `getUserMedia` is unavailable and there is nothing to send. SpeakerButton
 * hides itself there; this URL builder stays correct for fleet mode and for
 * any future secure direct deployment.
 *
 * @param arcHost When set (e.g. `selectedARC.host` in fleet mode), used before the single-ARC env hostname.
 */
export function getLiveAudioWsUrl(
    arcHost?: string | null,
    isSecure: boolean = isHttpSecure(),
    arcId?: string | null,
): string | null {
    const explicit = process.env.REACT_APP_LIVE_AUDIO_WS_URL?.trim();
    if (explicit) {
        // Operator overrides are supplied as complete URLs. In fleet mode the
        // override must already contain /arc/<serial>/api/audio; never append
        // the legacy ?arc= selector to it.
        return explicit.replace(/\/$/, "");
    }
    const host =
        process.env.REACT_APP_LIVE_AUDIO_HOST?.trim() ||
        arcHost?.trim() ||
        process.env.REACT_APP_SINGLE_ARC_HOSTNAME?.trim() ||
        process.env.REACT_APP_HOST_OVERRIDE?.trim() ||
        (typeof window !== "undefined" ? window.location.hostname : "");
    if (!host) {
        return null;
    }
    // Direct and fleet mode both use the path-aware gateway helper. Callers
    // that need authentication should use getLiveAudioWsUrlWithToken below.
    const proxyHost =
        process.env.REACT_APP_LIVE_AUDIO_HOST?.trim() ||
        arcHost?.trim();
    const gateway = gatewayWebSocketUrl("audio", null, arcId, proxyHost);
    if (gateway) return gateway;

    if (arcId) return null;
    const protocol = isSecure ? "wss" : "ws";
    const port = process.env.REACT_APP_LIVE_AUDIO_PORT?.trim() || DEFAULT_LIVE_AUDIO_PORT;
    return `${protocol}://${host}:${port}`;
}

/**
 * As getLiveAudioWsUrl, but with the ARC JWT attached for direct mode.
 *
 * The gateway's forward_auth protects /api/audio, and a WebSocket handshake
 * cannot carry an Authorization header, so the token rides in the query string.
 * arc_auth's `_extract_token` reads Authorization, then cookie, then `?token=`.
 *
 * Fleet and direct mode both carry the token in the WebSocket query string.
 */
export function getLiveAudioWsUrlWithToken(
    token?: string | null,
    arcHost?: string | null,
    isSecure: boolean = isHttpSecure(),
    arcId?: string | null,
): string | null {
    const proxyHost =
        process.env.REACT_APP_LIVE_AUDIO_HOST?.trim() ||
        arcHost?.trim();
    const gateway = gatewayWebSocketUrl("audio", token, arcId, proxyHost);
    if (gateway) return gateway;
    return getLiveAudioWsUrl(arcHost, isSecure, arcId);
}
